<script setup lang="ts">
import { computed } from 'vue';
import { FILLED_ICONS, ICON_PATHS, type IconName } from '../icons';

/**
 * Renders an icon from the built-in set.
 *
 * Decorative by default: `aria-hidden` unless a `label` is supplied. An icon beside a
 * text label that is *also* announced makes a screen reader say everything twice, and
 * an icon-only button with no label is unusable — so the choice is explicit.
 */
const props = withDefaults(
  defineProps<{
    name: IconName;
    size?: number | string;
    /** Accessible name. Omit when the icon sits beside visible text. */
    label?: string;
    strokeWidth?: number;
  }>(),
  { size: 18, strokeWidth: 1.75 },
);

const path = computed(() => ICON_PATHS[props.name]);
const filled = computed(() => FILLED_ICONS.has(props.name));
const dimension = computed(() => (typeof props.size === 'number' ? `${props.size}px` : props.size));
</script>

<template>
  <svg
    :width="dimension"
    :height="dimension"
    viewBox="0 0 24 24"
    :fill="filled ? 'currentColor' : 'none'"
    :stroke="filled ? 'none' : 'currentColor'"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    :aria-hidden="label === undefined ? 'true' : undefined"
    :role="label === undefined ? undefined : 'img'"
    :aria-label="label"
    class="shrink-0"
  >
    <title v-if="label">{{ label }}</title>
    <path :d="path" />
  </svg>
</template>
